import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { LogOut, ChevronRight } from "lucide-react";

const PATH_LABELS: Record<string, string> = {
  "/": "Panel",
  "/vehicles": "Vehículos",
  "/vehicles/motorcycles": "Motocicletas",
  "/vehicles/automobiles": "Automóviles",
  "/vehicles/new": "Nuevo",
  "/vehicles/edit": "Editar",
  "/parts": "Repuestos",
  "/parts/new": "Nuevo",
  "/parts/edit": "Editar",
  "/users": "Usuarios",
  "/users/new": "Nuevo",
  "/users/edit": "Editar",
  "/quotes": "Cotizaciones",
  "/quotes/new": "Nueva",
  "/quotes/edit": "Editar",
  "/quote-requests": "Solicitudes Web",
  "/financings": "Financiamiento",
  "/payments": "Pagos",
  "/garantias": "Garantías",
  "/raffles": "Rifas",
  "/raffles/new": "Nueva",
  "/raffles/edit": "Editar",
  "/notifications": "Notificaciones",
  "/suggestions": "Sugerencias",
  "/configuracion": "Ajustes",
  "/documentacion": "Documentación",
  "/more": "Más",
};

/** Parent path for the "back" link of each section */
const SECTION_PARENTS: Record<string, string> = {
  vehicles: "/vehicles/motorcycles",
  parts: "/parts",
  users: "/users",
  quotes: "/quotes",
  financings: "/financings",
  payments: "/payments",
  raffles: "/raffles",
};

interface Crumb {
  label: string;
  href?: string;
  isLast: boolean;
}

function getBreadcrumbs(pathname: string): Crumb[] {
  if (pathname === "/") {
    return [{ label: "Panel", isLast: true }];
  }

  const segments = pathname.split("/").filter(Boolean);
  const crumbs: Crumb[] = [{ label: "Panel", href: "/", isLast: false }];

  let currentPath = "";
  for (let i = 0; i < segments.length; i++) {
    currentPath += `/${segments[i]}`;
    const isLast = i === segments.length - 1;

    // Check if this is a dynamic ID segment (not a known path)
    const isIdSegment =
      !PATH_LABELS[currentPath] &&
      i > 0 &&
      segments[i] !== "new" &&
      segments[i] !== "edit";

    if (isIdSegment) {
      // For "edit/:id" show "Editar" label, for detail "/:id" show "Detalle"
      const parentSegment = segments[i - 1];
      if (parentSegment === "edit") {
        // Skip — the "edit" crumb already added "Editar"
        continue;
      }
      crumbs.push({
        label: "Detalle",
        isLast,
      });
    } else {
      const label = PATH_LABELS[currentPath] ?? segments[i];
      crumbs.push({
        label,
        href: isLast ? undefined : currentPath,
        isLast,
      });
    }
  }

  return crumbs;
}

export function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const breadcrumbs = getBreadcrumbs(location.pathname);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const emailInitial = user?.email?.charAt(0).toUpperCase() ?? "?";

  return (
    <header className="flex h-14 items-center justify-between border-b border-border/50 bg-background/80 backdrop-blur-sm px-6">
      <nav className="flex items-center gap-1 text-sm">
        {breadcrumbs.map((crumb, index) => (
          <span key={index} className="flex items-center gap-1">
            {index > 0 && (
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/60" />
            )}
            {crumb.href && !crumb.isLast ? (
              <Link
                to={crumb.href}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                {crumb.label}
              </Link>
            ) : (
              <span
                className={
                  crumb.isLast
                    ? "font-semibold text-foreground"
                    : "text-muted-foreground"
                }
              >
                {crumb.label}
              </span>
            )}
          </span>
        ))}
      </nav>

      <div className="flex items-center gap-3">
        <span className="text-sm text-muted-foreground">{user?.email}</span>
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-sm font-medium text-primary">
          {emailInitial}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleLogout}
          className="gap-2 text-muted-foreground hover:bg-primary/10 hover:text-primary"
        >
          <LogOut className="h-4 w-4" />
          Cerrar sesión
        </Button>
      </div>
    </header>
  );
}
